/**
 * The world leaderboards: the periods a board can be cut over, the metrics it can rank by, and
 * where a period begins.
 *
 * Shared because both halves have to agree about the *same* window. The client names a period
 * and a metric in its request and prints the window it is looking at; the server aggregates
 * `match_results` over that window and keys its cache by its start. Two definitions of "this
 * week" would be a page whose heading and whose rows described different weeks — and, worse, a
 * cache keyed by one of them and filled from the other.
 *
 * ## The clock is UTC, and the week starts Monday
 *
 * Every boundary here is computed in UTC, never in the viewer's zone. A world leaderboard is one
 * list that everybody is on, so "today" has to mean the same day to a player in Auckland and a
 * player in Los Angeles; a per-viewer day would give the same run two different ranks and make
 * the cache — one entry per (metric, period, start) for the whole process — impossible.
 *
 * The week starts **Monday** 00:00 UTC, which is ISO-8601's week and what the rest of the world
 * outside the US means by "this week". It is named here rather than assumed because
 * `Date.getUTCDay()` counts from Sunday, so the Monday shift is a line of code somebody could
 * quietly write differently.
 *
 * A period is a half-open interval `[start, now]`: a run that finished at exactly 00:00:00.000
 * UTC belongs to the day that is starting, not the one that just ended. The hour is cut the same
 * way — a run that ended at exactly 13:00:00.000 is the first run of the 13:00 hour.
 */

/** The windows a board can be cut over, in the order a selector draws them. */
export const LEADERBOARD_PERIODS = ['hour', 'day', 'week', 'month', 'year', 'all'] as const;

export type LeaderboardPeriod = (typeof LEADERBOARD_PERIODS)[number];

/**
 * What a board ranks by.
 *
 * Two of these are the best a player managed in a *single* run and three are what they piled up
 * across the period, which is the distinction the labels beside them have to keep making:
 *
 * - `mass`       — the largest a single run ever got (a peak, not the mass it ended on)
 * - `pellets`    — the most dots swallowed in one run
 * - `dots`       — total dots swallowed in the period, across every run
 * - `kills`      — total players eaten in the period
 * - `deaths`     — times eaten in the period; runs that ended by quitting or by the room closing
 *                  are endings rather than deaths and are not counted
 * - `massGained` — total mass received in the period, from pellets, cells and duels alike
 * - `matches`    — runs finished in the period
 *
 * **`pellets` and `dots` are the same column read two different ways**, and nothing but their
 * labels says so: `pellets` is `max(pellets_eaten)` — the best single run — and `dots` is
 * `sum(pellets_eaten)` over the window. They are separate metrics rather than one because "the
 * biggest meal anybody has ever eaten" and "who has eaten the most today" are different
 * questions with different winners, and a board that quietly answered one while claiming the
 * other would be worse than having neither. Every reader that prints one has to say which.
 *
 * Every one of them is an aggregate over `match_results` rows, which is why they can be re-cut
 * by period at all; `player_stats` holds the lifetime rollups and cannot answer "this month".
 */
export const LEADERBOARD_METRICS = [
  'mass',
  'pellets',
  'dots',
  'kills',
  'deaths',
  'massGained',
  'matches',
] as const;

export type LeaderboardMetric = (typeof LEADERBOARD_METRICS)[number];

/**
 * How many ranked players a board serves at most.
 *
 * The client pages within this with an offset, so it is also the depth a "load more" can reach.
 * 500 is 25 pages at `PAGINATION.DEFAULT_LIMIT` — past the point anybody scrolls, and small
 * enough that the whole board is one cached array per (metric, period).
 */
export const LEADERBOARD_BOARD_SIZE = 500;

/**
 * The metrics a champions feed leads with.
 *
 * A subset rather than all seven, because a champions call is one cached board per (metric,
 * period) and a banner that named a title-holder for every metric would be a banner nobody
 * reads. These four are the ones a player recognises instantly: the biggest blob, the most dots,
 * the most chomps and — the one the report asked for by name — the most deaths, which is a title
 * worth holding precisely because it is funny.
 */
export const LEADERBOARD_CHAMPION_METRICS = ['mass', 'dots', 'kills', 'deaths'] as const;

/**
 * How many (metric, period) titles one champions call may ask for.
 *
 * Each combination is one entry in the process-wide board cache, so a caller naming every metric
 * against every period would be asking one process to hold — and refresh — the whole grid on
 * their behalf. The default fits inside this with room to spare.
 */
export const LEADERBOARD_MAX_CHAMPIONS = 12;

/**
 * The narrowest cached board's TTL, in milliseconds — the floor a client's champions poll has
 * to stay above.
 *
 * It is `LEADERBOARD_HOUR_CACHE_TTL_MS` in `services/leaderboards/board.ts`, named here because
 * the only reader that needs it is the browser and the server's own module is not importable
 * from one. A champions call issues no SQL — it reads the process's board cache — so polling
 * faster than this buys nothing at all and costs a request per client per tick of the interval.
 */
export const LEADERBOARD_CHAMPION_CACHE_FLOOR_MS = 5_000;

/**
 * How often a client re-asks for the champions feed.
 *
 * Six times the floor above, because a title is a slow fact: the hour's top dog changes when
 * somebody finishes a bigger run, not fifteen times a second, and this is polled by *every*
 * client in every room and on the landing page at once. The callout rotates between the titles
 * it already has on a timer of its own, so what a reader sees changes far more often than what
 * is asked for.
 */
export const LEADERBOARD_CHAMPION_POLL_MS = 30_000;

/**
 * The periods a death screen shows, widest window last.
 *
 * Five of the six — the year is left out, because it sits between "this month" and "all time"
 * and says very little that either does not. The first `LEADERBOARD_STANDING_COMPACT` of them
 * are what a phone keeps; see `DeathScreen`.
 */
export const LEADERBOARD_STANDING_PERIODS = ['hour', 'day', 'week', 'month', 'all'] as const;

/**
 * How many of those a narrow screen draws.
 *
 * Two, plus all time, which is never dropped: the death panel is translucent and deliberately
 * does not fill a phone's viewport, so the rows past this are the first thing worth giving up
 * to keep the arena visible around it.
 */
export const LEADERBOARD_STANDING_COMPACT = 2;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The first millisecond of `period` containing `nowMs`, or `null` for all time — which is the
 * absence of a lower bound rather than a very old one, so the query drops the predicate entirely
 * instead of comparing every row against the epoch.
 *
 * Pure and UTC, so it is the same answer in every process and on every machine.
 */
export function leaderboardPeriodStartMs(period: LeaderboardPeriod, nowMs: number): number | null {
  const now = new Date(nowMs);
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const date = now.getUTCDate();

  switch (period) {
    case 'hour':
      return Date.UTC(year, month, date, now.getUTCHours());
    case 'day':
      return Date.UTC(year, month, date);
    case 'week': {
      // `getUTCDay()` is 0 for Sunday; this is how many days back Monday is.
      const sinceMonday = (now.getUTCDay() + 6) % 7;
      return Date.UTC(year, month, date) - sinceMonday * DAY_MS;
    }
    case 'month':
      return Date.UTC(year, month, 1);
    case 'year':
      return Date.UTC(year, 0, 1);
    case 'all':
      return null;
  }
}
